import {consume} from "@lit/context";
import {LitElement, html, nothing} from "lit";
import {customElement} from "lit/decorators.js";
import {unsafeStatic} from "lit/static-html.js";
import type {LDClient, LDContext} from "launchdarkly-js-client-sdk";

import {ROOT_TAG} from "../../globals";
import {BridgeBarConfigContext} from "../../config/BridgeBarConfigContext";
import type {BridgeBarConfig, Deployment, Theme} from "../../config";
import {BridgeBarDataContext} from "../../backend";
import type {BridgeBarDataContextValue} from "../../backend/BridgeBarData";
import {getBbEnv} from "../../lib/bbEnv";
import {features, LDClientContext} from "../../launchdarkly";
import type {LDClientContextValue} from "../../launchdarkly";

/** Custom element tag name. */
const tagName = `${ROOT_TAG}-notifications-bell`;

/** Static reference for use in Lit html template literals. */
export const NotificationsBell = unsafeStatic(tagName);

/** External notifications component tag. */
const NOTIFICATIONS_TAG = "enterprise-product-experience-notifications";

/** File served under each deployment's static-hosting CDN. */
const NOTIFICATIONS_SCRIPT_FILE = "bridgebar-notifications.esm.js";

/** New Lit bundle; registers the same custom element name and attribute contract as the legacy bundle. */
const NOTIFICATIONS_LIT_SCRIPT_FILE = "bridgebar-notifications-lit.esm.js";

/** URL path segment the Lit bundle is served under, on the same CDN host. */
const NOTIFICATIONS_LIT_CDN_PATH = "lit";

const BUNDLE_MARKER_ATTRIBUTE = "data-bb-notifications-bundle";
const LIT_BUNDLE_MARKER = "lit";
const STENCIL_BUNDLE_MARKER = "stencil";

/** CDN host for the external notifications ES module, per Bridge deployment. */
const NOTIFICATIONS_CDN_HOST: Record<Deployment, string> = {
  dz: "static.notifications.dangerzone.coxautoservices.com",
  live: "static.notifications.coxautoservices.com"
};

/** Bridge `theme` values map to the notifications component's `bb_theme` tokens. */
const BB_THEME_MAP: Record<Theme, string> = {
  light: "Interstate-Light",
  dark: "Interstate-Dark"
};

interface LitViewEvaluation {
  value: boolean;
  reason?: string;
}

/**
 * Wraps the external `<enterprise-product-experience-notifications>` component
 * in a Lit shadow DOM. Injects the external bundle's `<script>` into the
 * document head from the `updated()` lifecycle hook so host pages do not need
 * to include it. The notifications element is created once — after both the
 * config context and the data context (platformId) are ready — because the
 * external component isn't designed to react to attribute changes after
 * instantiation. Element creation is try/catch isolated: if its constructor
 * throws, the notifications area renders empty without propagating errors.
 */
@customElement(tagName)
export class NotificationsBellElement extends LitElement {
  @consume({context: BridgeBarConfigContext, subscribe: true})
  private _config: BridgeBarConfig | undefined;

  @consume({context: BridgeBarDataContext, subscribe: true})
  private _dataContext: BridgeBarDataContextValue | undefined;

  @consume({context: LDClientContext, subscribe: true})
  private _ldContextValue: LDClientContextValue | undefined;

  /**
   * Cached notifications element. Created once both contexts are ready and
   * then re-used across renders, so the external component isn't re-mounted
   * on every Lit update.
   */
  private notificationsEl: HTMLElement | null = null;

  private observedLdClient: LDClient | undefined;

  private mismatchWarned = false;

  private readonly handleLdFlagsChanged = (): void => {
    this.requestUpdate();
  };

  private _readLdContext(ldClient: LDClient): LDContext | undefined {
    try {
      return ldClient.getContext();
    } catch {
      return undefined;
    }
  }

  private _evaluateLitViewFlag(
    ldClient: LDClient
  ): LitViewEvaluation | undefined {
    const flag = features.pdt.notificationsLitView;
    if (typeof ldClient.variationDetail !== "function") {
      return {value: ldClient.variation(flag, false) === true};
    }

    const detail = ldClient.variationDetail(flag, false);
    if (
      detail?.reason?.kind === "ERROR" &&
      detail.reason.errorKind === "CLIENT_NOT_READY"
    ) {
      return undefined;
    }
    return {
      value: detail?.value === true,
      reason: detail?.reason?.errorKind ?? detail?.reason?.kind
    };
  }

  /**
   * Loads whichever bundle — legacy or Lit — the notifications-lit-view flag
   * selects. Idempotent, and one-shot per page: both bundles register the same
   * custom element name and a custom element cannot be un-defined, so
   * whichever loads first wins.
   */
  private _ensureScriptLoaded(config: BridgeBarConfig): void {
    const host = NOTIFICATIONS_CDN_HOST[config.deployment];
    if (!host) return;

    const ldClient = this._ldContextValue?.ldClient;
    if (!ldClient) return;

    const ldContext = this._ldContextValue?.ldContext;
    if (!ldContext) return;

    const contextFields = ldContext as unknown as Record<string, unknown>;
    if (contextFields.anonymous) return;

    const liveContext = this._readLdContext(ldClient);
    if (liveContext) {
      const liveFields = liveContext as unknown as Record<string, unknown>;
      if (liveFields.anonymous) return;
    }

    const evaluation = this._evaluateLitViewFlag(ldClient);
    if (!evaluation) return;
    const litViewEnabled = evaluation.value;

    const stencilSrc = `https://${host}/${NOTIFICATIONS_SCRIPT_FILE}`;
    const litSrc = `https://${host}/${NOTIFICATIONS_LIT_CDN_PATH}/${NOTIFICATIONS_LIT_SCRIPT_FILE}`;

    const litLoaded = this._isBundleLoaded(
      NOTIFICATIONS_LIT_SCRIPT_FILE,
      LIT_BUNDLE_MARKER
    );
    const stencilLoaded = this._isBundleLoaded(
      NOTIFICATIONS_SCRIPT_FILE,
      STENCIL_BUNDLE_MARKER
    );

    if (litLoaded || stencilLoaded) {
      this._warnOnBundleMismatch(litLoaded, stencilLoaded, litViewEnabled);
      return;
    }

    const script = document.createElement("script");
    script.type = "module";
    script.src = litViewEnabled ? litSrc : stencilSrc;
    script.setAttribute(
      BUNDLE_MARKER_ATTRIBUTE,
      litViewEnabled ? LIT_BUNDLE_MARKER : STENCIL_BUNDLE_MARKER
    );
    document.head.appendChild(script);

    // eslint-disable-next-line no-console
    console.info("[NotificationsBellElement] notifications bundle selected", {
      bundle: litViewEnabled ? LIT_BUNDLE_MARKER : STENCIL_BUNDLE_MARKER,
      flag: features.pdt.notificationsLitView,
      flagValue: litViewEnabled,
      flagReason: evaluation.reason,
      ldContextApplicationId: contextFields.applicationId,
      applicationId: config.applicationId,
      deployment: config.deployment
    });
  }

  private _isBundleLoaded(scriptFile: string, marker: string): boolean {
    return !!document.querySelector(
      `script[src*="${scriptFile}"], script[${BUNDLE_MARKER_ATTRIBUTE}="${marker}"]`
    );
  }

  private _warnOnBundleMismatch(
    litLoaded: boolean,
    stencilLoaded: boolean,
    litViewEnabled: boolean
  ): void {
    if (this.mismatchWarned) return;

    if (litLoaded && stencilLoaded) {
      this.mismatchWarned = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[NotificationsBellElement] both the Lit and the legacy notifications ` +
          `bundles are loaded on this page. Both register ${NOTIFICATIONS_TAG} ` +
          `and a custom element cannot be re-defined, so which view renders is ` +
          `whichever script executed first and cannot be determined here. The ` +
          `host page is including a notifications bundle that Bridge Bar also ` +
          `injects — remove one of them.`
      );
      return;
    }

    if (litViewEnabled ? litLoaded : stencilLoaded) return;
    this.mismatchWarned = true;
    // eslint-disable-next-line no-console
    console.warn(
      `[NotificationsBellElement] ${
        litViewEnabled ? "Lit" : "legacy"
      } notifications view is enabled, but the ${
        litLoaded ? "Lit" : "legacy"
      } bundle is already loaded on this page. Custom elements cannot be ` +
        `re-defined, so the flag has no effect until reload. Check whether ` +
        `the host page includes the notifications bundle itself.`
    );
  }

  /**
   * Creates the external notifications element with all attributes set from
   * the current config + data context. Returns null if creation throws (e.g.
   * the registered element's constructor failed).
   */
  private _createNotificationsEl(
    config: BridgeBarConfig,
    bbEnv: string,
    platformId: string
  ): HTMLElement | null {
    try {
      // For unregistered tags, createElement returns an HTMLUnknownElement
      // (no render, no throw). createElement only throws if a registered
      // element's constructor itself throws — the try/catch guards that case.
      const el = document.createElement(NOTIFICATIONS_TAG);
      el.setAttribute("bb_env", bbEnv);
      el.setAttribute("bb_lang", config.language ?? "en");
      el.setAttribute("bb_session", config.sessionId);
      el.setAttribute("bb_user", platformId);
      if (config.theme) {
        el.setAttribute("bb_theme", BB_THEME_MAP[config.theme]);
      }
      return el;
    } catch (error) {
      // Swallow — other Bridge Bar components remain functional.
      // eslint-disable-next-line no-console
      console.error(
        `[NotificationsBellElement] Failed to create ${NOTIFICATIONS_TAG}:`,
        error
      );
      return null;
    }
  }

  /**
   * Uses `updated()` (not `connectedCallback()`) so this re-runs once
   * `_ldContextValue` resolves after identify.
   */
  override updated(): void {
    this._subscribeToLdFlagChanges();
    if (this._config) {
      this._ensureScriptLoaded(this._config);
    }
  }

  private _subscribeToLdFlagChanges(): void {
    const ldClient = this._ldContextValue?.ldClient;
    if (!ldClient || ldClient === this.observedLdClient) return;

    this._unsubscribeFromLdFlagChanges();
    ldClient.on("ready", this.handleLdFlagsChanged);
    ldClient.on("change", this.handleLdFlagsChanged);
    this.observedLdClient = ldClient;
  }

  private _unsubscribeFromLdFlagChanges(): void {
    this.observedLdClient?.off("ready", this.handleLdFlagsChanged);
    this.observedLdClient?.off("change", this.handleLdFlagsChanged);
    this.observedLdClient = undefined;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._unsubscribeFromLdFlagChanges();
  }

  override render() {
    if (!this._config) return nothing;
    if (this._dataContext?.status !== "complete") return nothing;

    const bbEnv = getBbEnv(this._config.deployment, this._config.partition);
    if (!bbEnv) return nothing;

    const platformId = this._dataContext.data.id;
    if (!platformId) return nothing;

    // Cache the element so re-renders reuse the same instance instead of
    // recreating and reloading the external notifications component.
    if (!this.notificationsEl) {
      this.notificationsEl = this._createNotificationsEl(
        this._config,
        bbEnv,
        platformId
      );
    }

    if (!this.notificationsEl) return nothing;
    return html`${this.notificationsEl}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: NotificationsBellElement;
  }
}
